/* =============================================================================
   MASKHY — newsletter pop-up (sections/newsletter-popup.liquid)
   Opens once the visitor has scrolled down a couple of screens. Signing up posts Shopify's
   customer form in the background; on success the code is revealed and pre-applied to the
   checkout (/discount/CODE). If Shopify asks for a challenge (bot check) the form is posted
   normally instead, and the page comes back with the pop-up open on the result.
   Remembers (per browser): subscribed → never again; closed → away for "snooze" days.
   ============================================================================= */
(function () {
  'use strict';

  const dlg = document.querySelector('[data-nl-popup]');
  if (!dlg) return;

  const KEY = 'maskhy:newsletter';
  const html = document.documentElement;
  const code = dlg.dataset.code;
  const snoozeDays = Number(dlg.dataset.snooze) || 14;
  const form = dlg.querySelector('form');
  const email = dlg.querySelector('input[type="email"]');
  const submit = dlg.querySelector('[data-nl-submit]');
  const errorEl = dlg.querySelector('[data-nl-error]');
  const join = dlg.querySelector('[data-nl-join]');
  const done = dlg.querySelector('[data-nl-done]');
  const designMode = !!(window.MaskhyTheme && window.MaskhyTheme.designMode);
  const reduce = () => !html.classList.contains('motion-ok');

  const store = {
    get() {
      try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
    },
    set(v) {
      try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* private mode: fine */ }
    },
  };

  function eligible() {
    const s = store.get();
    if (!s) return true;
    if (s.state === 'subscribed') return false;
    return Date.now() - (s.at || 0) > snoozeDays * 864e5;
  }

  /* ---- open / close ------------------------------------------------------------------ */
  function open() {
    if (dlg.open) return;
    if (typeof dlg.showModal === 'function') dlg.showModal();
    else dlg.setAttribute('open', '');
    html.classList.add('has-popup');
    requestAnimationFrame(() => requestAnimationFrame(() => dlg.classList.add('is-in')));
  }

  function close(remember = true) {
    if (!dlg.open) return;
    if (remember && !designMode && (store.get() || {}).state !== 'subscribed') store.set({ state: 'closed', at: Date.now() });
    dlg.classList.remove('is-in');
    const finish = () => {
      if (typeof dlg.close === 'function') dlg.close();
      else dlg.removeAttribute('open');
      html.classList.remove('has-popup');
    };
    if (reduce()) finish();
    else setTimeout(finish, 380);
  }

  dlg.addEventListener('cancel', (e) => { e.preventDefault(); close(); }); // Esc
  dlg.addEventListener('click', (e) => {
    if (e.target === dlg) return close(); // the dimmed backdrop
    if (e.target.closest('[data-nl-close]')) return close();
    if (e.target.closest('[data-nl-shop]')) close(false); // then the in-page link runs (mk-nav.js)
  });

  /* ---- states ------------------------------------------------------------------------ */
  function showDone() {
    store.set({ state: 'subscribed', at: Date.now() });
    join.hidden = true;
    done.hidden = false;
    dlg.classList.add('is-done');
    // Pre-apply the code: Shopify keeps it in a cookie and adds it at checkout.
    fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin', redirect: 'follow' }).catch(() => {});
    const glass = dlg.querySelector('[data-nl-glass]');
    if (glass) glass.focus({ preventScroll: true });
  }

  function setBusy(busy) {
    submit.disabled = busy;
    submit.classList.toggle('is-busy', busy);
  }

  function showError(text) {
    errorEl.textContent = text;
    errorEl.hidden = !text;
    if (text) email.setAttribute('aria-invalid', 'true');
    else email.removeAttribute('aria-invalid');
  }

  if (form) {
    email.addEventListener('input', () => showError(''));
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!form.reportValidity()) return;
      setBusy(true);
      showError('');
      try {
        const res = await fetch(form.action, {
          method: 'POST',
          body: new URLSearchParams(new FormData(form)),
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        if (res.ok && /customer_posted=true/.test(res.url)) {
          setBusy(false);
          showDone();
          return;
        }
      } catch (err) { /* network: fall through to a normal post */ }
      // Challenge, validation error or anything unexpected: let Shopify handle it as a page.
      form.submit();
    });
  }

  /* ---- copy the code ------------------------------------------------------------------ */
  const copyBtn = dlg.querySelector('[data-nl-copy]');
  if (copyBtn) {
    copyBtn.addEventListener('click', async () => {
      const label = copyBtn.querySelector('[data-nl-copy-label]');
      const original = label.textContent;
      try {
        await navigator.clipboard.writeText(code);
      } catch (e) {
        const range = document.createRange();
        range.selectNodeContents(dlg.querySelector('[data-nl-code]'));
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        return;
      }
      label.textContent = copyBtn.dataset.copied || original;
      copyBtn.classList.add('is-done');
      setTimeout(() => { label.textContent = original; copyBtn.classList.remove('is-done'); }, 1800);
    });
  }

  /* ---- came back from a normal post: show the result ------------------------------------- */
  if (dlg.querySelector('[data-nl-posted]')) {
    open();
    showDone();
    return;
  }
  if (dlg.querySelector('[data-nl-errors]')) {
    open();
    return;
  }

  /* ---- theme editor: open while the section is selected ------------------------------------ */
  if (designMode) {
    document.addEventListener('shopify:section:select', (e) => { if (e.detail.sectionId === dlg.dataset.sectionId) open(); });
    document.addEventListener('shopify:section:deselect', (e) => { if (e.detail.sectionId === dlg.dataset.sectionId) close(false); });
    return;
  }

  /* ---- the trigger: scrolling down past N screens (or half of a short page) ------------- */
  if (!eligible()) return;
  let lastY = window.scrollY;
  let ticking = false;
  function check() {
    ticking = false;
    const y = window.scrollY;
    const down = y > lastY;
    lastY = y;
    if (!down) return;
    const screens = Number(dlg.dataset.threshold) || 2;
    const room = document.documentElement.scrollHeight - window.innerHeight;
    const at = Math.min(screens * window.innerHeight, room * 0.5);
    if (y < at) return;
    // never on top of the cart, the menu or another dialog
    if (html.classList.contains('has-drawer') || html.style.overflow === 'hidden' || document.querySelector('dialog[open]')) return;
    window.removeEventListener('scroll', onScroll);
    open();
  }
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(check);
  }
  window.addEventListener('scroll', onScroll, { passive: true });
})();
